import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Save, RotateCcw, Loader2, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Apparatus catalog. The id is what gets persisted to the backend.
// For BOTH-discipline competitions we expose explicit WAG/MAG variants of
// the shared apparatus (vault, floor) so admins can tell them apart.
const APPARATUS_CATALOG = {
  // WAG-only
  bars:     { label: 'Bars',  color: 'bg-purple-100 text-purple-800' },
  beam:     { label: 'Beam',  color: 'bg-pink-100 text-pink-800' },
  range:    { label: 'Range', color: 'bg-orange-100 text-orange-800' },
  // MAG-only
  rings:    { label: 'Rings',  color: 'bg-amber-100 text-amber-800' },
  pommel:   { label: 'Pommel', color: 'bg-yellow-100 text-yellow-800' },
  pbars:    { label: 'P-Bars', color: 'bg-rose-100 text-rose-800' },
  hbar:     { label: 'H-Bar',  color: 'bg-fuchsia-100 text-fuchsia-800' },
  // Generic (used for WAG-only or MAG-only competitions)
  vault:    { label: 'Vault', color: 'bg-blue-100 text-blue-800' },
  floor:    { label: 'Floor', color: 'bg-green-100 text-green-800' },
  // Discipline-specific shared apparatus (BOTH competitions)
  wagvault: { label: 'WAG Vault', color: 'bg-blue-100 text-blue-800' },
  magvault: { label: 'MAG Vault', color: 'bg-sky-100 text-sky-800' },
  wagfloor: { label: 'WAG Floor', color: 'bg-green-100 text-green-800' },
  magfloor: { label: 'MAG Floor', color: 'bg-emerald-100 text-emerald-800' },
  // Other
  rest:     { label: 'Rest', color: 'bg-gray-200 text-gray-700' },
};

const getApparatusList = (discipline) => {
  const d = (discipline || 'WAG').toUpperCase();
  if (d === 'BOTH') {
    return [
      'wagvault', 'bars', 'beam', 'wagfloor', 'range',
      'magvault', 'magfloor', 'rings', 'pommel', 'pbars', 'hbar',
      'rest',
    ].map(id => ({ id, ...APPARATUS_CATALOG[id] }));
  }
  if (d === 'MAG') {
    return [
      'vault', 'floor', 'rings', 'pommel', 'pbars', 'hbar', 'rest',
    ].map(id => ({ id, ...APPARATUS_CATALOG[id] }));
  }
  // WAG default
  return [
    'vault', 'bars', 'beam', 'floor', 'range', 'rest',
  ].map(id => ({ id, ...APPARATUS_CATALOG[id] }));
};

const getApparatusMeta = (id) => APPARATUS_CATALOG[id] || { label: id, color: '' };

const DEFAULT_ROT_COUNT = 5;

export default function RotationScheduleEditor({ competitionId, token, discipline }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [programme, setProgramme] = useState(null);
  const [selectedRound, setSelectedRound] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [originalSchedule, setOriginalSchedule] = useState([]);

  const apparatusList = getApparatusList(discipline);
  const isBoth = (discipline || 'WAG').toUpperCase() === 'BOTH';

  // Load programme for this competition
  useEffect(() => {
    const loadProgramme = async () => {
      try {
        const response = await axios.get(
          `${API}/superadmin/programmes/${competitionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProgramme(response.data);
        if (response.data?.rounds?.length > 0) {
          setSelectedRound(response.data.rounds[0].roundNumber.toString());
        }
      } catch (error) {
        console.error('Error loading programme:', error);
        toast.error('Failed to load programme');
      } finally {
        setLoading(false);
      }
    };

    if (competitionId && token) {
      loadProgramme();
    }
  }, [competitionId, token]);

  // Load rotation schedule when round changes
  useEffect(() => {
    const loadSchedule = async () => {
      if (!selectedRound || !programme) return;

      try {
        const response = await axios.get(
          `${API}/superadmin/rotation-schedule/${competitionId}/${selectedRound}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const round = programme.rounds?.find(r => r.roundNumber.toString() === selectedRound);
        const groupObjects = round?.groups || [];
        const groupNames = groupObjects.map(g => g.groupName || g);

        const existingGroups = response.data?.groups || [];
        const scheduleData = groupNames.map(groupName => {
          const existingGroup = existingGroups.find(g => g.groupName === groupName);
          if (existingGroup) {
            return existingGroup;
          }
          return {
            groupName,
            rotations: Array.from({ length: DEFAULT_ROT_COUNT }, (_, i) => ({
              rotationNumber: i + 1,
              apparatus: ''
            }))
          };
        });

        setSchedule(scheduleData);
        setOriginalSchedule(JSON.parse(JSON.stringify(scheduleData)));
      } catch (error) {
        console.error('Error loading rotation schedule:', error);
        toast.error('Failed to load rotation schedule');
      }
    };

    loadSchedule();
  }, [selectedRound, programme, competitionId, token]);

  const handleApparatusChange = (groupIndex, rotationIndex, apparatus) => {
    const newSchedule = [...schedule];
    newSchedule[groupIndex].rotations[rotationIndex].apparatus = apparatus;
    setSchedule(newSchedule);
  };

  const addRotationSlot = (groupIndex) => {
    setSchedule(prev => {
      const next = [...prev];
      const grp = { ...next[groupIndex], rotations: [...next[groupIndex].rotations] };
      const num = grp.rotations.length + 1;
      grp.rotations.push({ rotationNumber: num, apparatus: '' });
      next[groupIndex] = grp;
      return next;
    });
  };

  const removeRotationSlot = (groupIndex, rotationIndex) => {
    setSchedule(prev => {
      const next = [...prev];
      const grp = { ...next[groupIndex], rotations: next[groupIndex].rotations.filter((_, i) => i !== rotationIndex) };
      // Re-number
      grp.rotations = grp.rotations.map((r, i) => ({ ...r, rotationNumber: i + 1 }));
      next[groupIndex] = grp;
      return next;
    });
  };

  const handleSave = async () => {
    for (const group of schedule) {
      const emptyRotations = group.rotations.filter(r => !r.apparatus);
      if (emptyRotations.length > 0) {
        toast.error(`Group ${group.groupName} has unassigned rotations`);
        return;
      }
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/superadmin/rotation-schedule/${competitionId}/${selectedRound}`,
        { groups: schedule },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOriginalSchedule(JSON.parse(JSON.stringify(schedule)));
      toast.success('Rotation schedule saved successfully');
    } catch (error) {
      console.error('Error saving rotation schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to save rotation schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSchedule(JSON.parse(JSON.stringify(originalSchedule)));
    toast.info('Changes reset');
  };

  const hasChanges = JSON.stringify(schedule) !== JSON.stringify(originalSchedule);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (!programme || !programme.rounds?.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          No programme found. Please create a programme with rounds and groups first.
        </CardContent>
      </Card>
    );
  }

  // Calculate max rotation count to render columns
  const maxRotations = Math.max(0, ...schedule.map(g => g.rotations.length));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rotation Schedule</CardTitle>
        <CardDescription>
          Assign apparatus to each rotation for each group. Each group can have a different number of rotations.
          Choose <strong>Rest</strong> for a rest slot. MAG apparatus (Rings, Pommel, P-Bars, H-Bar) are scored from the <code>/mag</code> page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <Label>Round:</Label>
          <Select value={selectedRound} onValueChange={setSelectedRound}>
            <SelectTrigger className="w-40" data-testid="rotation-round-select">
              <SelectValue placeholder="Select round" />
            </SelectTrigger>
            <SelectContent>
              {programme.rounds.map((round) => (
                <SelectItem key={round.roundNumber} value={round.roundNumber.toString()}>
                  Round {round.roundNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {schedule.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border p-3 text-left font-semibold">Group</th>
                  {Array.from({ length: maxRotations }, (_, i) => (
                    <th key={i} className="border p-3 text-center font-semibold">
                      Rotation {i + 1}
                    </th>
                  ))}
                  <th className="border p-3 text-center font-semibold w-12">Add</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((group, groupIndex) => (
                  <tr key={group.groupName} className="hover:bg-gray-50">
                    <td className="border p-3 font-medium">{group.groupName}</td>
                    {Array.from({ length: maxRotations }, (_, rotationIndex) => {
                      const rotation = group.rotations[rotationIndex];
                      if (!rotation) {
                        return <td key={rotationIndex} className="border p-2 bg-gray-50"></td>;
                      }
                      const appColor = getApparatusMeta(rotation.apparatus).color;
                      return (
                        <td key={rotationIndex} className="border p-2">
                          <div className="flex items-center gap-1">
                            <Select
                              value={rotation.apparatus}
                              onValueChange={(value) => handleApparatusChange(groupIndex, rotationIndex, value)}
                            >
                              <SelectTrigger
                                className={`w-full ${rotation.apparatus ? appColor : ''}`}
                                data-testid={`apparatus-select-${group.groupName}-${rotationIndex}`}
                              >
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {apparatusList.map((apparatus) => (
                                  <SelectItem key={apparatus.id} value={apparatus.id}>
                                    {apparatus.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {group.rotations.length > 1 && (
                              <button
                                onClick={() => removeRotationSlot(groupIndex, rotationIndex)}
                                className="text-gray-400 hover:text-red-600 p-1"
                                title="Remove rotation"
                                data-testid={`remove-rotation-${group.groupName}-${rotationIndex}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="border p-2 text-center">
                      <button
                        onClick={() => addRotationSlot(groupIndex)}
                        className="text-gray-500 hover:text-black"
                        title="Add rotation slot"
                        data-testid={`add-rotation-${group.groupName}`}
                      >
                        <Plus className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-4">
            No groups defined for this round. Please add groups to the programme first.
          </p>
        )}

        {schedule.length > 0 && (
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={!hasChanges || saving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-black hover:bg-gray-800"
              data-testid="save-rotation-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Schedule
            </Button>
          </div>
        )}

        <div className="pt-4 border-t">
          <p className="text-sm text-gray-500 mb-2">Apparatus Legend:</p>
          <div className="flex flex-wrap gap-2">
            {apparatusList.map((apparatus) => (
              <span key={apparatus.id} className={`px-2 py-1 rounded text-xs font-medium ${apparatus.color}`}>
                {apparatus.label}
              </span>
            ))}
          </div>
          {isBoth && (
            <p className="text-xs text-gray-500 mt-2">
              For combined competitions, choose <strong>WAG Vault/Floor</strong> or <strong>MAG Vault/Floor</strong> so judges
              and the music control routes (e.g. <code>/wagfloor</code>, <code>/magfloor</code>) pick up the right group.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
